import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { usePermission } from "../../../features/auth/use-permission";
import { HistoryTabs } from "../../../features/change-history/components/history-tabs";
import { RecordHistory } from "../../../features/change-history/components/record-history";
import { historyTabSearchSchema } from "../../../features/change-history/history-search";
import { sourceSystemsQueryOptions } from "../../../features/source-systems/api/source-systems";
import { SourceSystemAdmin } from "../../../features/source-systems/components/source-system-admin";

/**
 * `RPM-092`'s `source_systems` surfaces (`J-296`): the list with the Key column, a platform's detail,
 * add, edit and deactivate (`RPM-082`'s Operator surface), and each platform's History (`RPM-063`).
 * The register is the tenant's, so a change asks `settings: ["update"]` at tenant scope, as the server
 * does (`source-systems.routes.ts`); the screen states a withheld change in words and the server
 * refuses it again whatever it showed. The tab is rendered only for an account holding
 * `audit: ["read"]` at tenant scope, where the history route reads it.
 */
function SourceSystemsScreen() {
  const { tab = "details" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const canChange = usePermission({ settings: ["update"] }, ["TENANT"]);
  const canReadHistory = usePermission({ audit: ["read"] }, ["TENANT"]);
  const platforms = useQuery({ ...sourceSystemsQueryOptions(), enabled: canReadHistory });
  return (
    <div className="flex flex-col gap-4 pt-4">
      <HistoryTabs
        showHistory={canReadHistory}
        tab={tab}
        onTabChange={(next) =>
          void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })
        }
        detailsLabel="Source systems"
        details={<SourceSystemAdmin canChange={canChange} />}
        history={
          <RecordHistory
            entity="source-systems"
            records={(platforms.data ?? []).map((p) => ({
              id: p.sourceSystemId,
              label: `${p.displayName} (${p.sourceSystemCode})`,
            }))}
            noun="Source system"
            caption={(label) => `Change history of source system ${label}`}
            columnLabels={{ display_name: "Source system", superseded_at: "Deactivated" }}
          />
        }
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/admin/source-systems")({
  validateSearch: historyTabSearchSchema,
  component: SourceSystemsScreen,
  staticData: { screenName: "Source systems" },
});
